/**
 * Enterprise expense tabs, folded into the existing ExpensesPage rather than
 * shipped as a second expense screen.
 *
 * Each tab is a thin read/act surface over /api/accounting/expenses. Server-side
 * permission guards remain authoritative; hiding a button here is UX only.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardBody } from '@/shared/components/ui/Card';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Select } from '@/shared/components/ui/Select';
import { Skeleton } from '@/shared/components/ui/Skeleton';
import toast from 'react-hot-toast';
import { getApiErrorMessage } from '@/shared/api/axios';
import { ExpenseListTab } from './ExpenseListTab';
import {
  ExpenseCategoryModal,
  RecurrenceModal,
  RecurrenceExecutionsModal,
  ReimbursementModal,
  CorporateCardModal,
} from './ExpenseSetupModals';
import {
  useExpenseDashboard,
  useExpenseCategories,
  useExpenseApprovalInbox,
  useExpenseRefunds,
  useExpenseRecurrences,
  useRecurrenceAction,
  useRunDueRecurrences,
  useReimbursements,
  useReimbursementBalances,
  usePayReimbursement,
  useCorporateCards,
  useCardTransactions,
  useSettleCard,
  useBillableLinks,
  useExpensesByDimension,
  useExpenseAgeing,
  useExpenseTaxReport,
  useGlReconciliation,
  useExpenseAction,
  useExpenseSummary,
  useArchiveExpenseCategory,
  type ExpenseCategory,
  type ExpenseRecurrence,
  type ReportDimension,
} from '../hooks/useExpenseApi';

import type { AdvancedExpenseTab } from './expenseTabs';

/** Minor units to a display string. Kept local so no formatting helper is duplicated. */
function money(minor: number | undefined, currency = 'INR'): string {
  const value = (Number(minor) || 0) / 100;
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency, maximumFractionDigits: 2 }).format(value);
}

function shortDate(value?: string | null): string {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: '2-digit' });
}

const CELL = 'px-3 py-2 align-middle';
const HEAD = 'px-3 py-2 text-xs font-medium uppercase tracking-wide text-slate-500';

/** Consistent empty state so every tab reads the same way when there is no data. */
function Empty({ title, hint }: { title: string; hint?: string }) {
  return (
    <div className="py-10 text-center">
      <p className="text-sm font-medium text-slate-600">{title}</p>
      {hint ? <p className="mt-1 text-xs text-slate-400">{hint}</p> : null}
    </div>
  );
}

function Loading() {
  return (
    <div className="space-y-2 py-4">
      <Skeleton className="h-8 w-full" />
      <Skeleton className="h-8 w-full" />
      <Skeleton className="h-8 w-full" />
    </div>
  );
}

/** Renders a failed query without leaking internals. */
function QueryError({ message }: { message?: string }) {
  return (
    <div className="py-8 text-center">
      <p className="text-sm font-medium text-rose-600">Could not load this view.</p>
      <p className="mt-1 text-xs text-slate-500">{message || 'Please retry in a moment.'}</p>
    </div>
  );
}

function KpiCard({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <Card>
      <CardBody>
        <p className="text-xs uppercase tracking-wide text-slate-500">{label}</p>
        <p className="mt-1 text-xl font-semibold text-slate-800">{value}</p>
        {sub ? <p className="mt-0.5 text-xs text-slate-400">{sub}</p> : null}
      </CardBody>
    </Card>
  );
}

// ── Overview ────────────────────────────────────────────────────────────────

function OverviewTab() {
  const { data, isLoading, isError, error } = useExpenseDashboard(6);

  if (isLoading) return <Loading />;
  if (isError) return <QueryError message={getApiErrorMessage(error)} />;
  if (!data) return <Empty title="No expense activity yet" hint="Capture an expense to populate these figures." />;

  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard label="Today" value={money(data.todaySpendMinor)} />
        <KpiCard label="This month" value={money(data.monthSpendMinor)} sub={`${data.monthExpenseCount} expense(s)`} />
        <KpiCard label="Outstanding" value={money(data.outstandingMinor)} sub="Unsettled balance" />
        <KpiCard label="Pending approval" value={String(data.pendingApprovalCount)} sub="Awaiting a decision" />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardBody>
            <h3 className="mb-2 text-sm font-semibold text-slate-700">Top categories this month</h3>
            {data.topCategories.length === 0 ? (
              <Empty title="No categorised spend yet" />
            ) : (
              <ul className="space-y-1.5">
                {data.topCategories.map((row) => (
                  <li key={row.label} className="flex items-center justify-between text-sm">
                    <span className="text-slate-600">{row.label}</span>
                    <span className="font-medium text-slate-800">
                      {money(row.grossMinor)} <span className="text-xs text-slate-400">({row.sharePercent}%)</span>
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <h3 className="mb-2 text-sm font-semibold text-slate-700">Top vendors this month</h3>
            {data.topVendors.length === 0 ? (
              <Empty title="No vendor spend yet" />
            ) : (
              <ul className="space-y-1.5">
                {data.topVendors.map((row) => (
                  <li key={row.label} className="flex items-center justify-between text-sm">
                    <span className="text-slate-600">{row.label}</span>
                    <span className="font-medium text-slate-800">{money(row.grossMinor)}</span>
                  </li>
                ))}
              </ul>
            )}
          </CardBody>
        </Card>
      </div>

      <Card>
        <CardBody>
          <h3 className="mb-2 text-sm font-semibold text-slate-700">Monthly trend</h3>
          {data.trend.length === 0 ? (
            <Empty title="Not enough history yet" />
          ) : (
            <div className="flex items-end gap-2" role="img" aria-label="Monthly expense trend">
              {data.trend.map((point) => {
                const peak = Math.max(...data.trend.map((p) => p.grossMinor), 1);
                const heightPct = Math.round((point.grossMinor / peak) * 100);
                return (
                  <div key={point.period} className="flex flex-1 flex-col items-center gap-1">
                    <div
                      className="w-full rounded-t bg-indigo-400"
                      style={{ height: `${Math.max(heightPct, 2)}px` }}
                      title={`${point.period}: ${money(point.grossMinor)}`}
                    />
                    <span className="text-[10px] text-slate-400">{point.period.slice(5)}</span>
                  </div>
                );
              })}
            </div>
          )}
        </CardBody>
      </Card>

      {data.recurringFailing.length > 0 ? (
        <Card>
          <CardBody>
            <h3 className="mb-2 text-sm font-semibold text-rose-600">Recurring profiles needing attention</h3>
            <ul className="space-y-1 text-sm">
              {data.recurringFailing.map((r) => (
                <li key={r._id} className="text-slate-600">
                  <span className="font-medium">{r.name}</span> — {r.lastError || 'paused after repeated failures'}
                </li>
              ))}
            </ul>
          </CardBody>
        </Card>
      ) : null}
    </div>
  );
}

// ── Categories ──────────────────────────────────────────────────────────────

function CategoriesTab() {
  const { data, isLoading, isError, error } = useExpenseCategories({ isActive: true });
  const archive = useArchiveExpenseCategory();
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<ExpenseCategory | null>(null);

  const openNew = () => { setEditing(null); setModalOpen(true); };
  const openEdit = (c: ExpenseCategory) => { setEditing(c); setModalOpen(true); };

  const modal = modalOpen ? (
    <ExpenseCategoryModal
      key={editing?._id ?? 'new'}
      open
      onClose={() => setModalOpen(false)}
      category={editing}
    />
  ) : null;

  if (isLoading) return <Loading />;
  if (isError) return <QueryError message={getApiErrorMessage(error)} />;

  const rows = data?.items ?? [];
  if (rows.length === 0) {
    return (
      <>
        <div className="mb-3 flex justify-end">
          <Button onClick={openNew}>New category</Button>
        </div>
        {modal}
        <Empty
          title="No expense categories configured"
          hint="Categories map an expense to a GL account and carry tax, receipt and ceiling defaults."
        />
      </>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <Button onClick={openNew}>New category</Button>
      </div>
      {modal}
      <table className="w-full text-left text-sm">
        <thead className="border-b border-slate-200">
          <tr>
            <th className={HEAD}>Code</th>
            <th className={HEAD}>Name</th>
            <th className={HEAD}>GL account</th>
            <th className={HEAD}>Tax</th>
            <th className={HEAD}>Ceiling</th>
            <th className={HEAD}>Flags</th>
            <th className={HEAD} />
          </tr>
        </thead>
        <tbody>
          {rows.map((c) => (
            <tr key={c._id} className="border-b border-slate-100">
              <td className={`${CELL} font-mono text-xs`}>{c.code}</td>
              <td className={CELL}>{c.name}</td>
              <td className={`${CELL} font-mono text-xs`}>{c.expenseAccountCode || '—'}</td>
              <td className={CELL}>
                {c.defaultTaxRate}% <span className="text-xs text-slate-400">{c.defaultTaxTreatment}</span>
              </td>
              <td className={CELL}>{c.maxAmountMinor > 0 ? money(c.maxAmountMinor) : 'None'}</td>
              <td className={`${CELL} text-xs text-slate-500`}>
                {[
                  c.requiresReceipt ? 'receipt required' : null,
                  c.isBillableByDefault ? 'billable' : null,
                  c.isReimbursableByDefault ? 'reimbursable' : null,
                  c.isCapital ? 'capital' : null,
                ].filter(Boolean).join(', ') || '—'}
              </td>
              <td className={CELL}>
                <div className="flex justify-end gap-1.5">
                  <Button size="sm" variant="ghost" onClick={() => openEdit(c)}>Edit</Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      if (!window.confirm(`Archive '${c.name}'? Historical expenses keep referencing it.`)) return;
                      archive.mutate(c._id, {
                        onSuccess: () => toast.success(`${c.name} archived`),
                        onError: (e: unknown) => toast.error(getApiErrorMessage(e) || 'Could not archive'),
                      });
                    }}
                    disabled={archive.isPending}
                  >
                    Archive
                  </Button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Approvals ───────────────────────────────────────────────────────────────

/** Exported so the unified Approval Inbox can render it alongside journals/PRs/POs without duplicating this logic. */
export function ApprovalsTab() {
  const { data, isLoading, isError, error } = useExpenseApprovalInbox();
  const action = useExpenseAction();
  const [rejecting, setRejecting] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());

  if (isLoading) return <Loading />;
  if (isError) return <QueryError message={getApiErrorMessage(error)} />;

  const rows = data?.items ?? [];
  if (rows.length === 0) {
    return <Empty title="Nothing awaiting your approval" hint="Submitted expenses you can decide on appear here." />;
  }

  const decide = (id: string, verb: 'approve' | 'reject', level: number, why?: string) => {
    action.mutate(
      { id, action: verb, body: verb === 'reject' ? { level, reason: why } : { level } },
      {
        onSuccess: () => {
          toast.success(verb === 'approve' ? 'Expense approved' : 'Expense rejected');
          setRejecting(null);
          setReason('');
        },
        onError: (e: unknown) => toast.error(getApiErrorMessage(e) || 'Could not record the decision'),
      },
    );
  };

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  /**
   * Bulk approve. Each expense is approved individually so one rejection does
   * not abort the batch, and the outcome is reported as a tally.
   */
  const bulkApprove = async () => {
    const targets = rows.filter(({ expense }) => selected.has(expense._id));
    if (!targets.length) return;

    let ok = 0;
    const failures: string[] = [];

    for (const { approval, expense } of targets) {
      try {
        await action.mutateAsync({ id: expense._id, action: 'approve', body: { level: approval.level } });
        ok += 1;
      } catch (e) {
        failures.push(`${expense.expenseNo}: ${getApiErrorMessage(e) || 'failed'}`);
      }
    }

    setSelected(new Set());
    if (ok) toast.success(`Approved ${ok} expense${ok === 1 ? '' : 's'}`);
    if (failures.length) toast.error(`${failures.length} could not be approved. ${failures[0]}`);
  };

  const selectedTotal = rows
    .filter(({ expense }) => selected.has(expense._id))
    .reduce((sum, { expense }) => sum + expense.grossAmountMinor, 0);

  return (
    <div className="space-y-3">
      {/* Bulk action bar, shown only when a selection exists. */}
      <div className="flex flex-wrap items-center justify-between gap-2 rounded border border-slate-200 bg-slate-50 px-3 py-2">
        <label className="flex items-center gap-2 text-sm text-slate-600">
          <input
            type="checkbox"
            checked={selected.size === rows.length && rows.length > 0}
            onChange={(e) =>
              setSelected(e.target.checked ? new Set(rows.map(({ expense }) => expense._id)) : new Set())
            }
            aria-label="Select all pending expenses"
          />
          {selected.size > 0 ? `${selected.size} selected · ${money(selectedTotal)}` : 'Select all'}
        </label>
        {selected.size > 0 ? (
          <div className="flex gap-2">
            <Button size="sm" onClick={bulkApprove} disabled={action.isPending}>
              Approve {selected.size}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())}>Clear</Button>
          </div>
        ) : null}
      </div>

      {rows.map(({ approval, expense }) => (
        <Card key={approval._id}>
          <CardBody>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={selected.has(expense._id)}
                  onChange={() => toggle(expense._id)}
                  aria-label={`Select ${expense.expenseNo}`}
                />
                <div>
                <p className="font-medium text-slate-800">
                  <Link
                    to={`/accounting-v2/expenses/${expense._id}`}
                    className="text-indigo-600 hover:underline"
                  >
                    {expense.expenseNo}
                  </Link>
                  {' — '}
                  {money(expense.grossAmountMinor, expense.currency)}
                </p>
                <p className="text-xs text-slate-500">
                  {shortDate(expense.expenseDate)} · {expense.description || expense.categoryLabel || 'Expense'} ·
                  level {approval.level} of {expense.requiredApprovalLevels || 1}
                </p>
                {expense.supplierName || expense.employeeName ? (
                  <p className="mt-0.5 text-xs text-slate-400">{expense.supplierName || expense.employeeName}</p>
                ) : null}
                </div>
              </div>
              <div className="flex gap-2">
                <Button size="sm" onClick={() => decide(expense._id, 'approve', approval.level)} disabled={action.isPending}>
                  Approve
                </Button>
                <Button size="sm" variant="secondary" onClick={() => setRejecting(expense._id)} disabled={action.isPending}>
                  Reject
                </Button>
              </div>
            </div>

            {rejecting === expense._id ? (
              <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-slate-100 pt-3">
                <Input
                  placeholder="Reason for rejection (required)"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  className="max-w-sm"
                  aria-label="Reason for rejection"
                />
                <Button
                  size="sm"
                  onClick={() => {
                    if (!reason.trim()) {
                      toast.error('A rejection reason is required');
                      return;
                    }
                    decide(expense._id, 'reject', approval.level, reason.trim());
                  }}
                  disabled={action.isPending}
                >
                  Confirm rejection
                </Button>
                <Button size="sm" variant="ghost" onClick={() => { setRejecting(null); setReason(''); }}>
                  Cancel
                </Button>
              </div>
            ) : null}
          </CardBody>
        </Card>
      ))}
    </div>
  );
}

// ── Reimbursements ──────────────────────────────────────────────────────────

function ReimbursementsTab() {
  const balances = useReimbursementBalances();
  const list = useReimbursements();
  const pay = usePayReimbursement();
  // Raising a claim is scoped to one employee, so the modal takes their id.
  const [claimFor, setClaimFor] = useState<{ id: string; name?: string } | null>(null);

  if (balances.isLoading || list.isLoading) return <Loading />;
  if (balances.isError) return <QueryError message={getApiErrorMessage(balances.error)} />;

  const outstanding = balances.data?.items ?? [];
  const claims = list.data?.items ?? [];

  return (
    <div className="space-y-4">
      {claimFor ? (
        <ReimbursementModal
          key={claimFor.id}
          open
          onClose={() => setClaimFor(null)}
          employeeId={claimFor.id}
          employeeName={claimFor.name}
        />
      ) : null}

      <Card>
        <CardBody>
          <h3 className="mb-2 text-sm font-semibold text-slate-700">Outstanding employee balances</h3>
          {outstanding.length === 0 ? (
            <Empty title="No employee is owed anything" hint="Balances build up when staff fund expenses personally." />
          ) : (
            <table className="w-full text-left text-sm">
              <thead className="border-b border-slate-200">
                <tr>
                  <th className={HEAD}>Employee</th>
                  <th className={HEAD}>Expenses</th>
                  <th className={HEAD}>Outstanding</th>
                  <th className={HEAD} />
                </tr>
              </thead>
              <tbody>
                {outstanding.map((b) => (
                  <tr key={b.employeeId} className="border-b border-slate-100">
                    <td className={CELL}>{b.employeeName || b.employeeId}</td>
                    <td className={CELL}>{b.expenseCount}</td>
                    <td className={`${CELL} font-medium`}>{money(b.outstandingMinor)}</td>
                    <td className={CELL}>
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => setClaimFor({ id: b.employeeId, name: b.employeeName })}
                      >
                        Raise claim
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardBody>
          <h3 className="mb-2 text-sm font-semibold text-slate-700">Reimbursement claims</h3>
          {claims.length === 0 ? (
            <Empty title="No claims raised yet" />
          ) : (
            <table className="w-full text-left text-sm">
              <thead className="border-b border-slate-200">
                <tr>
                  <th className={HEAD}>Number</th>
                  <th className={HEAD}>Employee</th>
                  <th className={HEAD}>Date</th>
                  <th className={HEAD}>Amount</th>
                  <th className={HEAD}>Status</th>
                  <th className={HEAD} />
                </tr>
              </thead>
              <tbody>
                {claims.map((r) => (
                  <tr key={r._id} className="border-b border-slate-100">
                    <td className={`${CELL} font-mono text-xs`}>{r.reimbursementNo}</td>
                    <td className={CELL}>{r.employeeName || r.employeeId}</td>
                    <td className={CELL}>{shortDate(r.reimbursementDate)}</td>
                    <td className={`${CELL} font-medium`}>{money(r.amountMinor)}</td>
                    <td className={CELL}>
                      <span className={`rounded px-2 py-0.5 text-xs ${
                        r.status === 'paid' ? 'bg-emerald-50 text-emerald-700'
                          : r.status === 'cancelled' ? 'bg-slate-100 text-slate-500'
                            : 'bg-amber-50 text-amber-700'
                      }`}>
                        {r.status}
                      </span>
                    </td>
                    <td className={CELL}>
                      {r.status === 'pending' ? (
                        <Button
                          size="sm"
                          onClick={() =>
                            pay.mutate({ id: r._id }, {
                              onSuccess: () => toast.success(`Reimbursement ${r.reimbursementNo} paid`),
                              onError: (e: unknown) => toast.error(getApiErrorMessage(e) || 'Payment failed'),
                            })
                          }
                          disabled={pay.isPending}
                        >
                          Mark paid
                        </Button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

// ── Corporate cards ─────────────────────────────────────────────────────────

function CardsTab() {
  const cards = useCorporateCards();
  const [selected, setSelected] = useState<string>('');
  const txns = useCardTransactions(selected ? { cardId: selected } : undefined);
  const settle = useSettleCard();
  const [registerOpen, setRegisterOpen] = useState(false);

  const registerModal = registerOpen ? (
    <CorporateCardModal open onClose={() => setRegisterOpen(false)} />
  ) : null;

  if (cards.isLoading) return <Loading />;
  if (cards.isError) return <QueryError message={getApiErrorMessage(cards.error)} />;

  const rows = cards.data?.items ?? [];
  if (rows.length === 0) {
    return (
      <>
        <div className="mb-3 flex justify-end">
          <Button onClick={() => setRegisterOpen(true)}>Register card</Button>
        </div>
        {registerModal}
        <Empty
          title="No corporate cards registered"
          hint="Card spend posts to clearing until the statement is settled."
        />
      </>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button onClick={() => setRegisterOpen(true)}>Register card</Button>
      </div>
      {registerModal}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {rows.map((c) => (
          <Card key={c.cardId}>
            <CardBody>
              <div className="flex items-start justify-between">
                <div>
                  <p className="font-medium text-slate-800">{c.cardName}</p>
                  <p className="text-xs text-slate-500">
                    {c.lastFourDigits ? `•••• ${c.lastFourDigits}` : 'No card number on file'}
                    {c.holderName ? ` · ${c.holderName}` : ''}
                  </p>
                </div>
                {!c.isActive ? <span className="text-xs text-slate-400">inactive</span> : null}
              </div>
              <p className="mt-2 text-lg font-semibold text-slate-800">{money(c.outstandingMinor)}</p>
              <p className="text-xs text-slate-400">
                {c.unsettledCount} unsettled
                {c.creditLimitMinor > 0 ? ` · limit ${money(c.creditLimitMinor)}` : ''}
              </p>
              <div className="mt-3 flex gap-2">
                <Button size="sm" variant="secondary" onClick={() => setSelected(c.cardId)}>
                  View lines
                </Button>
                {c.outstandingMinor > 0 ? (
                  <Button
                    size="sm"
                    onClick={() =>
                      settle.mutate({ id: c.cardId }, {
                        onSuccess: (r) => toast.success(`Settled ${money(r?.amountMinor)} across ${r?.transactionsSettled} line(s)`),
                        onError: (e: unknown) => toast.error(getApiErrorMessage(e) || 'Settlement failed'),
                      })
                    }
                    disabled={settle.isPending}
                  >
                    Settle
                  </Button>
                ) : null}
              </div>
            </CardBody>
          </Card>
        ))}
      </div>

      {selected ? (
        <Card>
          <CardBody>
            <div className="mb-2 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-slate-700">Card transactions</h3>
              <Button size="sm" variant="ghost" onClick={() => setSelected('')}>Close</Button>
            </div>
            {txns.isLoading ? <Loading /> : (txns.data?.items ?? []).length === 0 ? (
              <Empty title="No transactions imported for this card" />
            ) : (
              <table className="w-full text-left text-sm">
                <thead className="border-b border-slate-200">
                  <tr>
                    <th className={HEAD}>Date</th>
                    <th className={HEAD}>Merchant</th>
                    <th className={HEAD}>Reference</th>
                    <th className={HEAD}>Amount</th>
                    <th className={HEAD}>Match</th>
                  </tr>
                </thead>
                <tbody>
                  {(txns.data?.items ?? []).map((t) => (
                    <tr key={t._id} className="border-b border-slate-100">
                      <td className={CELL}>{shortDate(t.transactionDate)}</td>
                      <td className={CELL}>{t.merchantName || '—'}</td>
                      <td className={`${CELL} font-mono text-xs`}>{t.externalReference || '—'}</td>
                      <td className={`${CELL} font-medium`}>{money(t.amountMinor)}</td>
                      <td className={`${CELL} text-xs`}>{t.matchStatus}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardBody>
        </Card>
      ) : null}
    </div>
  );
}

// ── Refunds ─────────────────────────────────────────────────────────────────

function RefundsTab() {
  const { data, isLoading, isError, error } = useExpenseRefunds();

  if (isLoading) return <Loading />;
  if (isError) return <QueryError message={getApiErrorMessage(error)} />;

  const rows = data?.items ?? [];
  if (rows.length === 0) {
    return <Empty title="No refunds recorded" hint="A refund reverses recognised cost and its input tax proportionally." />;
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left text-sm">
        <thead className="border-b border-slate-200">
          <tr>
            <th className={HEAD}>Refund</th>
            <th className={HEAD}>Date</th>
            <th className={HEAD}>Net</th>
            <th className={HEAD}>Tax reversed</th>
            <th className={HEAD}>Gross</th>
            <th className={HEAD}>Reason</th>
            <th className={HEAD}>Type</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r._id} className="border-b border-slate-100">
              <td className={`${CELL} font-mono text-xs`}>{r.refundNo}</td>
              <td className={CELL}>{shortDate(r.refundDate)}</td>
              <td className={CELL}>{money(r.netAmountMinor)}</td>
              <td className={CELL}>{money(r.taxAmountMinor)}</td>
              <td className={`${CELL} font-medium`}>{money(r.grossAmountMinor)}</td>
              <td className={`${CELL} text-xs`}>{r.reason.replace(/_/g, ' ')}</td>
              <td className={`${CELL} text-xs`}>{r.isPartial ? 'partial' : 'full'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Billable ────────────────────────────────────────────────────────────────

function BillableTab() {
  const [status, setStatus] = useState('');
  const { data, isLoading, isError, error } = useBillableLinks(status ? { status } : undefined);

  if (isLoading) return <Loading />;
  if (isError) return <QueryError message={getApiErrorMessage(error)} />;

  const rows = data?.items ?? [];

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="max-w-[200px]"
          aria-label="Filter by billing status"
          options={[
            { value: '', label: 'All statuses' },
            { value: 'wip', label: 'Work in progress' },
            { value: 'invoiced', label: 'Invoiced' },
            { value: 'written_off', label: 'Written off' },
          ]}
        />
      </div>

      {rows.length === 0 ? (
        <Empty
          title="No billable costs tracked"
          hint="Mark an expense billable and link it to a project, then transfer it to WIP."
        />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-slate-200">
              <tr>
                <th className={HEAD}>Cost</th>
                <th className={HEAD}>Markup</th>
                <th className={HEAD}>Billable</th>
                <th className={HEAD}>Status</th>
                <th className={HEAD}>Invoice</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((l) => (
                <tr key={l._id} className="border-b border-slate-100">
                  <td className={CELL}>{money(l.costMinor)}</td>
                  <td className={CELL}>{l.markupPercent}%</td>
                  <td className={`${CELL} font-medium`}>{money(l.billableMinor)}</td>
                  <td className={`${CELL} text-xs`}>{l.status.replace(/_/g, ' ')}</td>
                  <td className={`${CELL} font-mono text-xs`}>{l.invoiceNo || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Reports ─────────────────────────────────────────────────────────────────

const DIMENSIONS: Array<{ value: ReportDimension; label: string }> = [
  { value: 'category', label: 'Category' },
  { value: 'vendor', label: 'Vendor' },
  { value: 'employee', label: 'Employee' },
  { value: 'project', label: 'Project' },
  { value: 'department', label: 'Department' },
  { value: 'costCenter', label: 'Cost centre' },
  { value: 'campaign', label: 'Campaign' },
  { value: 'currency', label: 'Currency' },
];

function ReportsTab() {
  const [dimension, setDimension] = useState<ReportDimension>('category');
  const summary = useExpenseSummary();
  const byDimension = useExpensesByDimension(dimension);
  const ageing = useExpenseAgeing();
  const tax = useExpenseTaxReport();
  const recon = useGlReconciliation();

  return (
    <div className="space-y-4">
      {/* Part 8 expense summary: recognised totals across the whole register. */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard
          label="Recognised"
          value={money(summary.data?.totals.grossMinor)}
          sub={`${summary.data?.totals.expenseCount ?? 0} expense(s)`}
        />
        <KpiCard label="Net of tax" value={money(summary.data?.totals.netMinor)} sub="Excludes recoverable tax" />
        <KpiCard label="Outstanding" value={money(summary.data?.totals.outstandingMinor)} sub="Not yet settled" />
        <KpiCard label="Refunded" value={money(summary.data?.totals.refundedMinor)} sub="Value returned" />
      </div>

      <Card>
        <CardBody>
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <h3 className="text-sm font-semibold text-slate-700">Expenses by dimension</h3>
            <Select
              value={dimension}
              onChange={(e) => setDimension(e.target.value as ReportDimension)}
              className="max-w-[200px]"
              aria-label="Report dimension"
              options={DIMENSIONS.map((d) => ({ value: d.value, label: d.label }))}
            />
          </div>

          {byDimension.isLoading ? <Loading /> : (byDimension.data?.rows ?? []).length === 0 ? (
            <Empty title="No spend to break down yet" />
          ) : (
            <table className="w-full text-left text-sm">
              <thead className="border-b border-slate-200">
                <tr>
                  <th className={HEAD}>{DIMENSIONS.find((d) => d.value === dimension)?.label}</th>
                  <th className={HEAD}>Net</th>
                  <th className={HEAD}>Tax</th>
                  <th className={HEAD}>Gross</th>
                  <th className={HEAD}>Count</th>
                  <th className={HEAD}>Share</th>
                </tr>
              </thead>
              <tbody>
                {(byDimension.data?.rows ?? []).map((row) => (
                  <tr key={row.label} className="border-b border-slate-100">
                    <td className={CELL}>{row.label}</td>
                    <td className={CELL}>{money(row.netMinor)}</td>
                    <td className={CELL}>{money(row.taxMinor)}</td>
                    <td className={`${CELL} font-medium`}>{money(row.grossMinor)}</td>
                    <td className={CELL}>{row.expenseCount}</td>
                    <td className={CELL}>{row.sharePercent}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardBody>
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardBody>
            <h3 className="mb-2 text-sm font-semibold text-slate-700">Ageing of unpaid expenses</h3>
            {ageing.isLoading ? <Loading /> : (ageing.data ?? []).length === 0 ? (
              <Empty title="Nothing outstanding" />
            ) : (
              <table className="w-full text-left text-sm">
                <thead className="border-b border-slate-200">
                  <tr>
                    <th className={HEAD}>Bucket</th>
                    <th className={HEAD}>Funded by</th>
                    <th className={HEAD}>Outstanding</th>
                  </tr>
                </thead>
                <tbody>
                  {(ageing.data ?? []).map((b, i) => (
                    <tr key={`${b.bucket}-${b.settlementMode}-${i}`} className="border-b border-slate-100">
                      <td className={CELL}>{b.bucket} days</td>
                      <td className={`${CELL} text-xs`}>{b.settlementMode.replace(/_/g, ' ')}</td>
                      <td className={`${CELL} font-medium`}>{money(b.outstandingMinor)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <h3 className="mb-2 text-sm font-semibold text-slate-700">Input tax</h3>
            {tax.isLoading ? <Loading /> : (tax.data?.rows ?? []).length === 0 ? (
              <Empty title="No tax recorded" />
            ) : (
              <>
                <table className="w-full text-left text-sm">
                  <thead className="border-b border-slate-200">
                    <tr>
                      <th className={HEAD}>Treatment</th>
                      <th className={HEAD}>Rate</th>
                      <th className={HEAD}>Tax</th>
                      <th className={HEAD}>Reclaim</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(tax.data?.rows ?? []).map((r, i) => (
                      <tr key={`${r.taxTreatment}-${r.taxRate}-${i}`} className="border-b border-slate-100">
                        <td className={`${CELL} text-xs`}>{r.taxTreatment.replace(/_/g, ' ')}</td>
                        <td className={CELL}>{r.taxRate}%</td>
                        <td className={CELL}>{money(r.taxMinor)}</td>
                        <td className={`${CELL} text-xs`}>{r.isRecoverable ? 'yes' : 'no'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p className="mt-2 text-xs text-slate-500">
                  Recoverable input tax: <span className="font-medium">{money(tax.data?.totals.recoverableTaxMinor)}</span>
                </p>
              </>
            )}
          </CardBody>
        </Card>
      </div>

      <Card>
        <CardBody>
          <h3 className="mb-2 text-sm font-semibold text-slate-700">General ledger reconciliation</h3>
          {recon.isLoading ? <Loading /> : !recon.data ? (
            <Empty title="Nothing to reconcile" />
          ) : (
            <>
              <div className="grid gap-3 sm:grid-cols-3">
                <KpiCard label="Per documents" value={money(recon.data.documents.netMinor)} sub={`${recon.data.documents.expenseCount} posted`} />
                <KpiCard label="Per ledger" value={money(recon.data.ledger.netMinor)} sub="Expense accounts, net" />
                <KpiCard
                  label="Variance"
                  value={money(recon.data.varianceMinor)}
                  sub={recon.data.reconciled ? 'Reconciled' : 'Needs investigation'}
                />
              </div>
              {!recon.data.reconciled ? (
                <p className="mt-3 rounded bg-amber-50 px-3 py-2 text-xs text-amber-800">{recon.data.note}</p>
              ) : null}
            </>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

// ── Recurring (V2) ──────────────────────────────────────────────────────────

export function RecurringV2Panel() {
  const { data, isLoading, isError, error } = useExpenseRecurrences();
  const action = useRecurrenceAction();
  const runDue = useRunDueRecurrences();
  const [createOpen, setCreateOpen] = useState(false);
  const [historyFor, setHistoryFor] = useState<ExpenseRecurrence | null>(null);

  if (isLoading) return <Loading />;
  if (isError) return <QueryError message={getApiErrorMessage(error)} />;

  const rows = data?.items ?? [];

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs text-slate-500">
          Recurring profiles generate draft expenses only; each one still goes through approval.
        </p>
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="secondary"
            onClick={() =>
              runDue.mutate(undefined, {
                onSuccess: (r) => toast.success(`Processed ${r?.processed ?? 0}, created ${r?.created ?? 0}`),
                onError: (e: unknown) => toast.error(getApiErrorMessage(e) || 'Run failed'),
              })
            }
            disabled={runDue.isPending}
          >
            Run all due
          </Button>
          <Button size="sm" onClick={() => setCreateOpen(true)}>New profile</Button>
        </div>
      </div>

      {createOpen ? <RecurrenceModal open onClose={() => setCreateOpen(false)} /> : null}
      {historyFor ? (
        <RecurrenceExecutionsModal
          key={historyFor._id}
          open
          onClose={() => setHistoryFor(null)}
          recurrence={historyFor}
        />
      ) : null}

      {rows.length === 0 ? (
        <Empty title="No recurring expense profiles" hint="Create one to generate a draft on a schedule." />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-slate-200">
              <tr>
                <th className={HEAD}>Name</th>
                <th className={HEAD}>Frequency</th>
                <th className={HEAD}>Next run</th>
                <th className={HEAD}>Generated</th>
                <th className={HEAD}>Status</th>
                <th className={HEAD} />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r._id} className="border-b border-slate-100">
                  <td className={CELL}>
                    {r.name}
                    {r.lastError ? <p className="text-xs text-rose-600">{r.lastError}</p> : null}
                  </td>
                  <td className={`${CELL} text-xs`}>{r.frequency}</td>
                  <td className={CELL}>{shortDate(r.nextRunDate)}</td>
                  <td className={CELL}>
                    {r.occurrencesGenerated}
                    {r.maxOccurrences > 0 ? ` / ${r.maxOccurrences}` : ''}
                  </td>
                  <td className={CELL}>
                    <span className={`rounded px-2 py-0.5 text-xs ${
                      r.status === 'active' ? 'bg-emerald-50 text-emerald-700'
                        : r.status === 'paused' ? 'bg-amber-50 text-amber-700'
                          : 'bg-slate-100 text-slate-500'
                    }`}>
                      {r.status}
                    </span>
                  </td>
                  <td className={CELL}>
                    <div className="flex gap-1.5">
                      {r.status === 'active' ? (
                        <>
                          <Button size="sm" variant="secondary" onClick={() => act(r._id, 'run')} disabled={action.isPending}>Run</Button>
                          <Button size="sm" variant="ghost" onClick={() => act(r._id, 'pause')} disabled={action.isPending}>Pause</Button>
                        </>
                      ) : null}
                      {r.status === 'paused' ? (
                        <Button size="sm" variant="secondary" onClick={() => act(r._id, 'resume')} disabled={action.isPending}>Resume</Button>
                      ) : null}
                      {r.status !== 'cancelled' && r.status !== 'completed' ? (
                        <Button size="sm" variant="ghost" onClick={() => act(r._id, 'cancel')} disabled={action.isPending}>Cancel</Button>
                      ) : null}
                      <Button size="sm" variant="ghost" onClick={() => setHistoryFor(r)}>History</Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  function act(id: string, verb: 'run' | 'pause' | 'resume' | 'cancel') {
    action.mutate({ id, action: verb }, {
      onSuccess: () => toast.success(`Profile ${verb === 'run' ? 'run' : verb + 'd'}`),
      onError: (e: unknown) => toast.error(getApiErrorMessage(e) || 'Action failed'),
    });
  }
}

import { useUser } from '@/shared/hooks/useAuth';

// ── Public entry ────────────────────────────────────────────────────────────

export function AdvancedExpenseTabs({ tab }: { tab: AdvancedExpenseTab }) {
  const user = useUser();
  const isEmployee = user?.role === 'employee';

  switch (tab) {
    // The register is the read/write surface over acc_expenses; every other tab
    // is downstream of an expense existing there.
    case 'register': return <ExpenseListTab />;
    case 'overview': return <OverviewTab />;
    case 'categories': return <CategoriesTab />;
    case 'approvals': return isEmployee ? <Empty title="Approvals restricted" hint="Approval management is restricted to administrators." /> : <ApprovalsTab />;
    case 'reimbursements': return <ReimbursementsTab />;
    case 'cards': return <CardsTab />;
    case 'refunds': return <RefundsTab />;
    case 'billable': return <BillableTab />;
    case 'reports': return <ReportsTab />;
    default: return <Empty title="Unknown tab" />;
  }
}
